import React, { useState } from "react";
import { FiCheck } from "react-icons/fi";
import ScrollAnimation from "react-animate-on-scroll";

// Reusable PricingCard Component
const PricingCard = ({
  title,
  price,
  priceType,
  features,
  buttonText,
  badgeText,
  buttonLink,
  badgeColor,
  description,
}) => {
  return (
    <div className="container">
      <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
        <div
          className="card border-danger mx-2 mb-4"
          style={{ maxWidth: "450px", borderRadius: "20px", overflow: "hidden" }}
        >
          <div className="card-header bg-color-blackest border-0 position-relative">
            <h4 className="mb-0 mt--10">
              <strong>{title}</strong>
            </h4>
            {badgeText && (
              <span
                className={`badge text-white position-absolute top-0 end-0 mt-2 me-2 bg-${badgeColor}`}
                style={{ fontSize: "11px", whiteSpace: "nowrap" }}
              >
                {badgeText}
              </span>
            )}
          </div>

          <div className="card-body bg-color-blackest" style={{ padding: "20px" }}>
            <p className="card-text">{description}</p>

            <h3>
              <strong className="theme-gradient">{price}</strong>{" "}
              <span className="text-muted" style={{ fontSize: "14px" }}>
                {priceType}
              </span>
            </h3>

            <ul className="list-unstyled mt-3 mb-4">
              {features.map((feature, index) => (
                <li className="d-flex align-items-center mb-2" key={index}>
                  <FiCheck className="text-danger me-2" /> {feature}
                </li>
              ))}
            </ul>

            <a
              href={buttonLink || "#"}
              target="_blank"
              className="btn-default w-100 text-white text-center fw-bold"
              style={{ borderRadius: "20px", fontSize: "16px" }}
              onClick={(e) => {
                if (!buttonLink ) {
                  e.preventDefault();
                  alert("Link is not available");
                }
              }}
            >
              {buttonText} →
            </a>
          </div>
        </div>
      </ScrollAnimation>
    </div>
  );
};

// Component with Toggle Functionality
const ERPSolutionsPricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const monthlyServices = [
    {
      title: "Basic Monthly",
      price: "৳ 3,000",
      priceType: "1 Months",
      features: [
        "1 Business Locations",
        "3 Users",
        "500 Products",
        "100 Invoices",
        "3 Trial Days",
      ],
      buttonText: "Register and Subscribe",
      buttonLink: "https://whatson.business/business/register?package=4",
      badgeText: "",
      badgeColor: "danger",
      description: "For small shops and startups business. Includes 1 location, 3 user, 500 products, sales & purchase, customer/supplier management, basic reports, and limited barcode printing for your business.",
    },
    {
      title: "Standard Monthly",
      price: "৳ 4,300",
      priceType: "1 Months",
      features: [
        "1 Business Locations",
        "5 Users",
        "5000 Products",
        "1000 Invoices",
        "5 Trial Days",
      ],
      buttonText: "Register and Subscribe",
      buttonLink: "https://whatson.business/business/register?package=5",
      badgeText: "Most Popular",
      badgeColor: "success",
      description: "For growing businesses. Supports 3 locations, 5 users, 5,000 products, full sales & purchase modules, stock management, barcode & label printing, reports, and email notifications.",
    },
    {
      title: "Premium Monthly",
      price: "৳ 6,500",
      priceType: "1 Months",
      features: [ 
        "Unlimited Business Locations",
        "Unlimited Users",
        "Unlimited Products",
        "Unlimited Invoices",
        "7 Trial Days",
      ],
      buttonText: "Register and Subscribe",
      buttonLink: "https://whatson.business/business/register?package=6",
      badgeText: "",
      badgeColor: "primary",
      description: "For enterprises and franchises. Unlimited locations, users, and products with all modules, advanced reports, custom invoices creation, API integrations with third-party tools, and priority support.",
    },
  ];

  const annualServices = [
    {
      title: "Basic Package",
      price: "৳ 30,000",
      priceType: "1 Years",
      features: [
        "1 Business Locations",
        "3 Users",
        "500 Products",
        "1000 Invoices",
        "3 Trial Days",
      ],
      buttonText: "Register and Subscribe",
      buttonLink: "https://whatson.business/business/register?package=1",
      badgeText: "",
      badgeColor: "success",
      description: "For small shops and startups business. Includes 1 location, 3 user, 500 products, sales & purchase, customer/supplier management, basic reports, and limited barcode printing for your business.",
    },
    {
      title: "Standard Package",
      price: "৳ 50,000",
      priceType: "1 Years",
      features: [
        "3 Business Locations",
        "5 Users",
        "5000 Products",
        "10000 Invoices",
        "5 Trial Days",
      ],
      buttonText: "Register and Subscribe",
      buttonLink: "https://whatson.business/business/register?package=2",
      badgeText: "Most Popular",
      badgeColor: "info",
      description: "For growing businesses. Supports 3 locations, 5 users, 5,000 products, full sales & purchase modules, stock management, barcode & label printing, reports, and email notifications.",
    },
    {
      title: "Premium Package",
      price: "৳ 70,000",
      priceType: "1 Years",
      features: [
        "Unlimited Business Locations",
        "Unlimited Users",
        "Unlimited Products",
        "Unlimited Invoices",
        "7 Trial Days",
      ],
      buttonText: "Register and Subscribe",
      buttonLink: "https://whatson.business/business/register?package=3",
      badgeText: "",
      badgeColor: "warning",
      description: "For enterprises and franchises. Unlimited locations, users, and products with all modules, advanced reports, custom invoices creation, API integrations with third-party tools, and priority support.",
    },
  ];

  const services = isAnnual ? annualServices : monthlyServices;

  return (
    <div className="container">
      {/* Toggle Switch */}
      <div className="d-flex justify-content-center align-items-center mb-4 gap-3">
        <span className={!isAnnual ? "fw-bold text-primary" : "text-muted"}>
          Monthly
        </span>

        <button
          onClick={() => setIsAnnual(!isAnnual)}
          className={`position-relative rounded-pill p-1 border-0`}
          style={{
            width: "60px",
            height: "28px",
            backgroundColor: isAnnual ? "#343a40" : "#343a40",
            transition: "0.3s",
          }}
        >
          <span
            className="rounded-circle bg-pink"
            style={{
              display: "block",
              width: "22px",
              height: "22px",
              backgroundColor: "#FB4353",
              position: "absolute",
              top: "3px",
              left: isAnnual ? "34px" : "3px",
              transition: "0.3s",
            }}
          />
        </button>

        <span className={isAnnual ? "fw-bold text-primary" : "text-muted"}>
          Annually
        </span>
      </div>

      {/* Pricing Cards */}
      <div className="row">
        {services.map((service, index) => (
          <div className="col-md-4" key={index}>
            <PricingCard {...service} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ERPSolutionsPricing;
